import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Mountain, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, formatAuthError } from "@/lib/api";

export default function VerifyContact() {
  const { user, home, startPhoneOtp, verifyPhoneOtp, clearPhoneOtp, resendEmailVerification, refreshMe } = useAuth();
  const nav = useNavigate();
  const [otp, setOtp] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [phoneResent, setPhoneResent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState("");

  const phoneVerified = Boolean(user?.phone_verified_at);
  const emailVerified = Boolean(user?.email_verified_at);
  const verified = phoneVerified || emailVerified;
  useEffect(() => () => clearPhoneOtp?.(), [clearPhoneOtp]);

  const resendPhoneOtp = async () => {
    setLoading("phone"); setError("");
    setPhoneResent(false);
    setOtp("");
    try {
      const delivery = await startPhoneOtp(user.phone);
      if (!delivery.sent) {
        setError(formatApiErrorDetail(delivery.error || "sms_send_failed"));
        return;
      }
      setPhoneResent(true);
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(""); }
  };

  const confirmPhone = async () => {
    setLoading("otp"); setError("");
    try {
      await verifyPhoneOtp(user.phone, otp);
      await refreshMe();
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(""); }
  };

  const resendEmail = async () => {
    setLoading("email"); setError("");
    try {
      await resendEmailVerification();
      setEmailSent(true);
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(""); }
  };

  return (
    <div className="tp-app-shell text-[#F4EFEB] antialiased" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-6 py-12">
        <Link to="/landing" className="mb-8 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#E5B85C]">
            <Mountain className="h-5 w-5 text-[#17211D]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>TrailPulse</span>
        </Link>

        <section className="tp-dark-panel rounded-[2rem] p-8">
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Verify account</h1>
          <p className="mt-1 text-sm text-[#94A3B8]">Use email or OTP.</p>

          <div className="mt-6 space-y-4">
            {user?.phone && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-start gap-3">
                  <Smartphone className="mt-0.5 h-5 w-5 text-[#E5B85C]" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold">Phone</p>
                    <p className="text-sm text-[#94A3B8]">{user.phone}</p>
                    {!phoneVerified && <p className="mt-1 text-xs text-[#8BE0C9]">{phoneResent ? "OTP sent." : "Request a code to verify your phone."}</p>}
                  </div>
                  {phoneVerified && <CheckCircle2 className="h-5 w-5 text-[#3FA98C]" />}
                </div>
                {!phoneVerified && (
                  <div className="mt-4 space-y-2">
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input aria-label="Phone OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" placeholder="6-digit code" className="min-h-10 flex-1 rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2 text-[#F4EFEB] outline-none focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
                      <button type="button" onClick={confirmPhone} disabled={Boolean(loading) || !otp} className="min-h-10 rounded-full bg-[#E5B85C] px-5 font-bold text-[#17211D] disabled:opacity-60">
                        {loading === "otp" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Verify OTP"}
                      </button>
                    </div>
                    <button type="button" onClick={resendPhoneOtp} disabled={Boolean(loading)} className="min-h-10 rounded-full border border-white/15 px-5 font-bold text-[#F4EFEB] hover:bg-white/10 disabled:opacity-60">
                      {loading === "phone" ? "Sending..." : phoneResent ? "Resend OTP" : "Send OTP"}
                    </button>
                    <p className="text-xs text-[#94A3B8]">By requesting a code, you agree to Google processing your phone number for authentication and abuse prevention.</p>
                  </div>
                )}
              </div>
            )}

            {user?.email && <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 text-[#E5B85C]" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold">Email</p>
                  <p className="break-all text-sm text-[#94A3B8]">{user?.email}</p>
                </div>
                {emailVerified && <CheckCircle2 className="h-5 w-5 text-[#3FA98C]" />}
              </div>
              {!emailVerified && (
                <>
                  <p className="mt-3 text-xs text-[#8BE0C9]">{emailSent ? "Email resent." : "Email sent."}</p>
                  <button type="button" onClick={resendEmail} disabled={Boolean(loading)} className="mt-4 min-h-10 rounded-full border border-white/15 px-5 font-bold text-[#F4EFEB] hover:bg-white/10 disabled:opacity-60">
                    {loading === "email" ? "Sending..." : "Resend email"}
                  </button>
                </>
              )}
            </div>}
          </div>

          {error && <div className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#E5695B]">{error}</div>}

          {!verified && <p className="mt-4 text-center text-sm font-semibold text-[#E5B85C]">Verify one method.</p>}

          <button type="button" onClick={() => nav(home)} disabled={!verified} className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] disabled:cursor-not-allowed disabled:opacity-50">
            Continue
          </button>
        </section>
      </div>
    </div>
  );
}
